'use client';

import React from 'react';
import { Tooltip } from './tooltip';
import { type VariantProps } from 'class-variance-authority';
import { buttonVariants } from './button-variants';
import { Loader2 } from 'lucide-react';
import { extendTailwindMerge } from 'tailwind-merge';

// Keep the new Comfort typography and named radii distinct from colour utilities.
const mergeButtonClasses = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        {
          text: [
            'display',
            'page-title',
            'section-title',
            'card-title',
            'body-lg',
            'body',
            'body-sm',
            'body-comfort',
            'caption',
            'eyebrow',
            'metric',
          ],
        },
      ],
      rounded: [{ rounded: ['control', 'control-comfort'] }],
    },
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  /** A reason makes the unavailable control focusable and prevents activation. */
  disabledReason?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, density, isLoading, children, disabled, disabledReason, ...props },
    ref,
  ) => {
    const reasonId = React.useId();
    const unavailable = Boolean(
      disabled ||
      isLoading ||
      disabledReason ||
      props['aria-disabled'] === true ||
      props['aria-disabled'] === 'true',
    );
    const describedReason = unavailable ? disabledReason : undefined;
    const button = (
      <button
        data-ui="button"
        className={mergeButtonClasses(
          buttonVariants({ variant, size, density, className }),
          'focus-visible:ring-focus',
          isLoading ? 'relative' : undefined,
        )}
        data-experience-button-variant={variant ?? 'primary'}
        ref={ref}
        disabled={unavailable && !describedReason}
        {...props}
        aria-disabled={unavailable || props['aria-disabled']}
        aria-describedby={
          [props['aria-describedby'], describedReason ? reasonId : undefined]
            .filter(Boolean)
            .join(' ') || undefined
        }
        onClick={(event) => {
          if (unavailable) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          props.onClick?.(event);
        }}
        onKeyDown={(event) => {
          if (unavailable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          props.onKeyDown?.(event);
        }}
        aria-busy={isLoading || props['aria-busy']}
      >
        {isLoading && (
          <span
            data-loading-spinner
            className="absolute right-0 top-1/2 -translate-y-1/2"
            aria-hidden="true"
          >
            <Loader2
              className="experience-candidate-motion !size-3 animate-spin"
              aria-hidden="true"
            />
          </span>
        )}
        {children}
      </button>
    );
    return describedReason ? (
      <>
        <Tooltip content={describedReason}>{button}</Tooltip>
        <span id={reasonId} className="sr-only">
          {describedReason}
        </span>
      </>
    ) : (
      button
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
